// Angular
import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Third party
import * as _ from 'lodash';
import moment from 'moment';

// Local library
import { IReplays } from '../+state/wager.models';
import { WagerFacade } from '../+state/wager.facade';

/**
 * Flattened replay row consumed by the UI table
 */
export interface IReplayRow {
  track?: string;
  trackId?: string | number;
  date?: string;
  breed?: string;
  race?: number | string;
  distance?: string;
  surface?: string;
  purse?: number | string;
}

/**
 * Normalized replays payload — table rows plus derived track list
 */
export interface IReplaysNormalized {
  rows: IReplayRow[];
  trackNames: string[];
}

/** Debounce for search dispatches (ms) */
const SEARCH_DEBOUNCE_MS = 300;

/** Debounce for runner-name lookups (ms) */
const RUNNER_LOOKUP_DEBOUNCE_MS = 300;

/** Minimum characters before triggering runner-name lookup */
const RUNNER_LOOKUP_MIN_CHARS = 3;

/**
 * Pure helpers + state coordination for the Replays and Search UI.
 * Owns form scaffolding, list flattening, dispatch debouncing and
 * replay-video kickoff so the component stays thin.
 */
@Injectable({ providedIn: 'root' })
export class ReplaysAndSearchService {

  private readonly formBuilder = inject(FormBuilder);
  private readonly wagerFacade = inject(WagerFacade);

  private searchTimeoutId?: ReturnType<typeof setTimeout>;
  private runnerLookupTimeoutId?: ReturnType<typeof setTimeout>;

  /**
   * Build the reactive form used by the Replays and Search view.
   * `dateBy` is stored as a real `Date` so the mat-datepicker can
   * round-trip it through its `DateAdapter` (a pre-formatted string
   * is rejected by `NativeDateAdapter.deserialize`).
   * @returns {FormGroup} form with dateBy, breed, runnerName, headToHead
   */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      dateBy: [new Date(), [Validators.required]],
      breed: ['0', [Validators.required]],
      runnerName: [''],
      headToHead: ['']
    });
  }

  /**
   * Flatten raw AvailableVideos payload into sorted table rows + track list
   * @param {IReplays | undefined} replays raw store payload
   * @returns {IReplaysNormalized} rows sorted by date desc, race asc, track asc
   */
  normalizeReplays(replays?: IReplays): IReplaysNormalized {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const available: any = (replays as any)?.response?.AvailableVideos;

    let rows: IReplayRow[] = [];

    // Structure: { eventKey: { breed: [ { EventName, TrackId, ... } ] } }
    _.flatMap(available, (event) => {
      _.map(event, (item, breed) => {
        _.map(item, (data) => {
          rows = [
            ...rows,
            {
              track: data?.EventName,
              trackId: data?.TrackId,
              date: data?.RaceDay,
              breed,
              race: data?.RaceId,
              distance: data?.Distance,
              surface: data?.Surface,
              purse: data?.Purse
            }
          ];
        });
      });
    });

    rows = _.orderBy(rows, ['date', 'race', 'track'], ['desc', 'asc', 'asc']);

    const trackNames = _.sortBy(
      _.uniq(_.map(rows, 'track')).filter(Boolean) as string[]
    );

    return { rows, trackNames };
  }

  /**
   * Client-side track filter (substring, case-insensitive)
   * @param {IReplayRow[]} rows source rows
   * @param {string} track track name to filter by
   * @returns {IReplayRow[]} filtered rows
   */
  filterByTrack(rows: IReplayRow[], track: string): IReplayRow[] {
    if (!track) return rows;

    const needle = track.toLowerCase();
    return _.filter(rows, (row) => {
      const trackName = row.track?.toLowerCase() || '';
      return trackName.includes(needle);
    });
  }

  /**
   * Fire an immediate (undebounced) replays fetch — used on init/clear
   * @param {string} sourceInitiator trace label
   * @param {FormGroup} form the current filters form
   * @returns {void}
   */
  fetchReplaysImmediate(sourceInitiator: string, form: FormGroup): void {
    this.wagerFacade.getReplays(sourceInitiator, this.toRequestPayload(form));
  }

  /**
   * Debounced replays fetch — used on filter/input change
   * @param {string} sourceInitiator trace label
   * @param {FormGroup} form the current filters form
   * @returns {void}
   */
  fetchReplaysDebounced(sourceInitiator: string, form: FormGroup): void {
    if (this.searchTimeoutId) clearTimeout(this.searchTimeoutId);

    this.searchTimeoutId = setTimeout(() => {
      this.wagerFacade.getReplays(sourceInitiator, this.toRequestPayload(form));
    }, SEARCH_DEBOUNCE_MS);
  }

  /**
   * Debounced runner-name autocomplete lookup
   * @param {string} prefix runner-name prefix
   * @param {() => void} onBelowMin callback when prefix is too short
   * @returns {boolean} true when a lookup was scheduled
   */
  lookupRunners(prefix: string, onBelowMin?: () => void): boolean {
    if (!prefix || prefix.length < RUNNER_LOOKUP_MIN_CHARS) {
      onBelowMin?.();
      return false;
    }

    if (this.runnerLookupTimeoutId) clearTimeout(this.runnerLookupTimeoutId);
    this.runnerLookupTimeoutId = setTimeout(() => {
      this.wagerFacade.getRunners(prefix);
    }, RUNNER_LOOKUP_DEBOUNCE_MS);

    return true;
  }

  /**
   * Reset the form to its initial values (today, All, empty inputs)
   * @param {FormGroup} form form to reset
   * @returns {void}
   */
  resetForm(form: FormGroup): void {
    form?.get('dateBy')?.setValue(new Date());
    form?.get('breed')?.setValue('0');
    form?.get('runnerName')?.setValue('');
    form?.get('headToHead')?.setValue('');
  }

  /**
   * Kick off a replay video for the selected row and stash its metadata
   * @param {IReplayRow} replay selected row
   * @param {string} [sourceInitiator] trace label
   * @returns {void}
   */
  playReplay(replay: IReplayRow, sourceInitiator = 'ReplaysAndSearchService:playReplay'): void {
    if (!replay) return;

    this.wagerFacade.getEventRaceVideoReplay(sourceInitiator, {
      toteEventId: replay?.trackId as any,
      toteRaceId: replay?.race as any,
      raceDay: this.toDate(new Date(replay?.date as string), 2),
      width: 640,
      height: 500
    } as any);

    this.wagerFacade.setSelectedReplayInfo(
      replay?.track as string,
      replay?.date as string,
      replay?.breed as string,
      replay?.race as any,
      undefined,
      replay?.trackId as any,
      'WIN',
      true
    );
  }

  /**
   * Format a Date for API/URL params
   * @param {Date} date input date
   * @param {number} format 1 = `YYYY-MM-DD`, 2 = `MM-DD-YYYY`
   * @returns {string} formatted date
   */
  toDate(date: Date, format: number = 1): string {
    const month = _.padStart(String(date.getMonth() + 1), 2, '0');
    const day = _.padStart(String(date.getDate()), 2, '0');

    return format === 2
      ? `${month}-${day}-${date.getFullYear()}`
      : `${date.getFullYear()}-${month}-${day}`;
  }

  /**
   * Clear any pending debounced timers (call from ngOnDestroy)
   * @returns {void}
   */
  clearTimers(): void {
    if (this.searchTimeoutId) {
      clearTimeout(this.searchTimeoutId);
      this.searchTimeoutId = undefined;
    }
    if (this.runnerLookupTimeoutId) {
      clearTimeout(this.runnerLookupTimeoutId);
      this.runnerLookupTimeoutId = undefined;
    }
  }

  /**
   * Build the GetRCNReplayVideosByFilters request payload from the form
   * @param {FormGroup} form current filters form
   * @returns {any} request payload
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toRequestPayload(form: FormGroup): any {
    const dateBy = form?.get('dateBy')?.value;

    return {
      raceDay: moment(dateBy).format('MM-DD-YYYY'),
      trackType: form?.get('breed')?.value,
      runnerName: form?.get('runnerName')?.value || '',
      runnerName2: form?.get('headToHead')?.value || ''
    };
  }
}
